<template>
  <v-card>
    <v-card-title class="headline">
      Cambiar mascota apadrinada
      <v-spacer />
      <v-btn icon :disabled="saving" @click="$emit('close-dialog')"><v-icon>mdi-close</v-icon></v-btn>
    </v-card-title>
    <v-divider />
    <v-card-text class="pt-4">
      <p>
        <strong>{{ sponsorship.name || '-' }}</strong> deja de estar apadrinada por este tutor.
        El plan y los cobros del apadrinamiento no cambian.
      </p>
      <v-autocomplete
        v-model="shelterPetId"
        :items="options"
        :loading="searching"
        :search-input.sync="search"
        item-text="label"
        item-value="id"
        label="Nueva mascota"
        placeholder="Busca por nombre"
        hint="Solo mascotas activas en la plataforma"
        persistent-hint
        no-filter
        outlined
        dense
        data-test="shelter-pet-select"
      />
      <v-alert v-if="changesOrganization" type="info" dense text class="mt-4 mb-0">
        La nueva mascota es de otra organización: se generará un nuevo certificado.
      </v-alert>
      <v-alert v-if="error" type="error" dense text class="mt-4 mb-0">{{ error }}</v-alert>
    </v-card-text>
    <v-card-actions class="justify-end">
      <v-btn text :disabled="saving" @click="$emit('close-dialog')">Cancelar</v-btn>
      <v-btn
        color="primary"
        depressed
        :disabled="!shelterPetId"
        :loading="saving"
        data-test="confirm-change"
        @click="confirm"
      >
        Cambiar mascota
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import ShelterPetsService from '@/services/shelter-pets.service';
import SponsorshipService from '@/services/sponsorship.service';

const SEARCH_LIMIT = 20;
const SEARCH_DELAY_MS = 300;

// Cambio de la mascota de un apadrinamiento desde la 360 (p.ej. la anterior fue adoptada).
// Se monta dentro de un v-dialog; emite `changed` al guardar y `close-dialog` al cancelar.
export default {
  name: 'ChangeSponsoredPetModal',
  props: {
    // Apadrinamiento de la 360: { id, name, organization, ... }.
    sponsorship: { type: Object, required: true }
  },
  emits: ['changed', 'close-dialog', 'submitting'],
  data() {
    return {
      search: '',
      options: [],
      shelterPetId: null,
      searching: false,
      saving: false,
      error: '',
      searchTimer: null
    };
  },
  computed: {
    selectedPet() {
      return this.options.find(pet => pet.id === this.shelterPetId) || null;
    },
    changesOrganization() {
      return Boolean(this.selectedPet) && this.selectedPet.organization !== this.sponsorship.organization;
    }
  },
  watch: {
    search(term) {
      // Al elegir una opción el autocomplete escribe su etiqueta: no hay que volver a buscar.
      if (this.selectedPet && term === this.selectedPet.label) return;
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => this.fetchPets(term), SEARCH_DELAY_MS);
    }
  },
  created() {
    this.fetchPets('');
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer);
  },
  methods: {
    async fetchPets(term) {
      this.searching = true;
      const res = await ShelterPetsService.getAll(1, SEARCH_LIMIT, { search: term || '', visible: true });
      this.searching = false;
      if (res.status !== 'ok') {
        this.error = 'No se pudieron cargar las mascotas';
        return;
      }
      const pets = (res.data.shelterPets || []).map(pet => {
        const organization = pet.organization ? pet.organization.name : '';
        return { id: pet.id, organization, label: organization ? `${pet.name} · ${organization}` : pet.name };
      });
      // La elegida se conserva aunque la nueva búsqueda no la traiga.
      this.options = this.selectedPet && !pets.some(pet => pet.id === this.selectedPet.id)
        ? [this.selectedPet, ...pets]
        : pets;
    },
    async confirm() {
      this.error = '';
      this.saving = true;
      this.$emit('submitting', true);
      const res = await SponsorshipService.changeShelterPet(this.sponsorship.id, this.shelterPetId);
      this.saving = false;
      this.$emit('submitting', false);
      if (res.status !== 'ok') {
        this.error = res.message;
        return;
      }
      this.$emit('changed');
    }
  }
};
</script>
